/**
 * AI Credits service (client portal). Talks to billing-service via the gateway
 * `/credits/*` ledger surface (tenant/user derived from the JWT). Powers the
 * top-bar chip, the Credits page (balance / monthly graph / per-action history /
 * purchase). Credits are integers — no decimals.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/api/client'
import { API_ENDPOINTS } from '@/lib/api/config'
import { getTenantId } from '@/lib/auth/keycloak'

// ── Types ──────────────────────────────────────────────────────────────────
export interface CreditPoolBalance {
  monthly: number      // plan credits (reset each month)
  purchased: number    // extra credits (carry over, never expire)
  monthly_quota: number
  total: number
}
export interface CreditBalance {
  account_id: string
  user_id: string | null
  quota_reset_date: string
  auto_reload_enabled: boolean
  ai: CreditPoolBalance
  email?: CreditPoolBalance
  document?: CreditPoolBalance
  image?: CreditPoolBalance
}

export interface UsageHistoryItem {
  id: string
  credit_type: string
  pool: 'monthly' | 'purchased'
  operation: 'credit' | 'debit'
  type: 'purchase' | 'usage' | 'transfer' | 'allocation' | 'refund'
  amount: number
  balance_after: number
  feature: string
  description: string | null
  model: string | null
  created_at: string
}
export interface UsageHistoryPage {
  items: UsageHistoryItem[]
  total: number
  page: number
  page_size: number
  has_more: boolean
}

export interface MonthlyUsage {
  tenant_id: string
  credit_type: string
  month: string
  days: { date: string; consumed: number; cumulative: number }[]
  plan_initial: number
  extra_purchased: number
  available: number
  total_consumed: number
  remaining: number
  prev_month: string
  prev_month_consumed: number
}

export interface CreditPackage {
  id: string
  name: string
  credit_type: string
  credits_amount: number
  bonus_amount: number
  price_cad: number
  is_best_value: boolean
  is_most_economical: boolean
}

export type HistoryKind = 'tous' | 'consommation' | 'achat' | 'transfert'

// ── Query keys (shared so any AI action can invalidate the balance) ──────────
export const creditKeys = {
  balance: ['credits', 'balance'] as const,
  monthly: (m?: string) => ['credits', 'monthly', m ?? 'current'] as const,
  history: (f: Record<string, unknown>) => ['credits', 'history', f] as const,
  packages: ['credits', 'packages'] as const,
}

// ── Hooks ────────────────────────────────────────────────────────────────────
export function useCreditBalance(options?: { realtime?: boolean }) {
  return useQuery({
    queryKey: creditKeys.balance,
    queryFn: () => apiClient.get('/credits/balance').then((r) => r.data as CreditBalance),
    staleTime: 10_000,
    // Real-time-ish: keep the top-bar count fresh.
    refetchInterval: options?.realtime ? 15_000 : false,
    refetchOnWindowFocus: true,
  })
}

export function useCreditUsageMonthly(month?: string, creditType = 'ai') {
  return useQuery({
    queryKey: creditKeys.monthly(month),
    queryFn: () =>
      apiClient
        .get('/credits/usage-monthly', { params: { month, credit_type: creditType } })
        .then((r) => r.data as MonthlyUsage),
    staleTime: 30_000,
  })
}

export function useCreditUsageHistory(filters: {
  kind?: HistoryKind
  feature?: string
  model?: string
  page?: number
  page_size?: number
  credit_type?: string
}) {
  const { kind = 'tous', page = 1, page_size = 10, credit_type = 'ai', feature, model } = filters
  return useQuery({
    queryKey: creditKeys.history({ kind, page, page_size, credit_type, feature, model }),
    queryFn: () =>
      apiClient
        .get('/credits/usage-history', {
          params: {
            credit_type,
            kind: kind === 'tous' ? undefined : kind,
            feature: feature || undefined,
            model: model || undefined,
            page,
            page_size,
          },
        })
        .then((r) => r.data as UsageHistoryPage),
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  })
}

export function useCreditPackages(creditType = 'ai') {
  return useQuery({
    queryKey: creditKeys.packages,
    queryFn: () =>
      apiClient
        .get('/credits/packages', { params: { credit_type: creditType } })
        .then((r) => (r.data?.items ?? []) as CreditPackage[]),
    staleTime: 5 * 60_000,
  })
}

export function usePurchaseCredits() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { package_id: string; quantity?: number; promo_code?: string }) =>
      apiClient.post('/credits/purchase', body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credits'] })
    },
  })
}

/** Start a Stripe V2 checkout for a credit package. Returns {checkout_url} to
 * redirect to Stripe, or {mode:'dev', credits_added} when Stripe isn't set.
 * #VOUCHER: an optional `promo_code` is validated + applied server-side (dev
 * grant includes the bonus; Stripe path carries it in metadata). */
export function useCheckoutCredits() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { package_id: string; success_url?: string; cancel_url?: string; promo_code?: string }) =>
      apiClient.post('/credits/checkout', body).then((r) => r.data as {
        mode: 'stripe' | 'dev'; status: string; checkout_url: string | null
        credits_added?: number; base_credits?: number; session_id?: string
        promo_applied?: boolean; promo_bonus?: number; promo_error?: string | null
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['credits'] }),
  })
}

// ── Coupons / vouchers ───────────────────────────────────────────────────────
export interface CouponValidation {
  valid: boolean
  error?: string
  code?: string
  discount_type?: 'PERCENTAGE' | 'FIXED_AMOUNT' | 'FREE_MONTHS' | string
  discount_value?: number
  duration?: string
  /** Concrete bonus credits for the previewed base (when base_credits sent). */
  bonus_credits?: number
  /** Concrete discount in cents for the previewed price (when amount_cents sent). */
  discount_cents?: number
  coupon?: Record<string, unknown>
}

/** Validate a promo/voucher code (optionally previewing the bonus/discount for a
 * given credit base or price). Talks to billing-service /billing/coupons/validate. */
export function useValidateCoupon() {
  return useMutation({
    mutationFn: (body: { code: string; plan_id?: string; profile?: string; base_credits?: number; amount_cents?: number }) =>
      apiClient.post('/billing/coupons/validate', body).then((r) => r.data as CouponValidation),
  })
}

/** Confirm a Stripe checkout on return (?session_id=...) and grant credits. */
export function useConfirmCheckout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (session_id: string) =>
      apiClient.post('/credits/confirm-checkout', { session_id }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['credits'] }),
  })
}

/** Imperative invalidation — call after any AI/ML action so the top-bar count
 * and credit pages refresh in real time. */
export function invalidateCredits(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['credits'] })
}

// ── Unified credits v2 ───────────────────────────────────────────────────────
// GET /api/v2/billing/credits/unified — single tenant AI-credit pool with a
// per-user ventilation breakdown. AI-only: only AI features consume credits.

/** Per-user share of the tenant's unified credit pool. */
export interface UnifiedUserVentilation {
  user_id: string
  email?: string | null
  name?: string | null
  consumed: number
  /** Optional per-user soft allocation/cap (null = no per-user cap). */
  allocation?: number | null
  share_pct?: number | null
}

/** One tenant-level AI-credit pool + per-user ventilation. */
export interface UnifiedCredits {
  tenant_id: string
  /** Plan credits (reset monthly). */
  monthly: number
  /** Extra purchased credits (carry over, never expire). */
  purchased: number
  /** Monthly plan allocation/quota. */
  monthly_quota: number
  /** Remaining = monthly + purchased. */
  total: number
  /** Total consumed this period across the whole tenant. */
  consumed: number
  quota_reset_date?: string | null
  auto_reload_enabled?: boolean
  /** Per-user ventilation of the single tenant pool. */
  users: UnifiedUserVentilation[]
}

/** Unified tenant credit pool + per-user ventilation (CreditsPage balance). */
export function useUnifiedCredits(options?: { realtime?: boolean }) {
  return useQuery({
    queryKey: ['credits', 'unified'] as const,
    queryFn: () =>
      apiClient.get(API_ENDPOINTS.billingV2.creditsUnified).then((r) => r.data as UnifiedCredits),
    staleTime: 10_000,
    refetchInterval: options?.realtime ? 15_000 : false,
    refetchOnWindowFocus: true,
  })
}

// ── Credit forecast v2 ───────────────────────────────────────────────────────
// GET /api/v1/credits/v2/forecast/{tenant} — burn-rate projection, projected
// exhaustion date and an upgrade/buy recommendation.

export type ForecastRecommendation = 'ok' | 'buy_extra' | 'upgrade'

export interface CreditForecast {
  tenant_id: string
  /** Average daily burn over the lookback window (credits/day). */
  burn_rate_daily: number
  /** Credits currently available (plan + extra). */
  available: number
  /** Days of runway left at the current burn rate (null = no measurable burn). */
  days_remaining: number | null
  /** ISO date the balance is projected to hit zero (null = no exhaustion). */
  projected_exhaustion_date: string | null
  /** Suggested action. */
  recommendation: ForecastRecommendation
  /** Human-readable rationale (FR). */
  message?: string | null
  /** Optional series for a small projection chart. */
  projection?: { date: string; projected_remaining: number }[]
}

/** Forecast for the current tenant (derived from the JWT tenant_id claim). */
export function useCreditForecast() {
  const tenant = getTenantId()
  return useQuery({
    queryKey: ['credits', 'forecast', tenant] as const,
    enabled: !!tenant,
    queryFn: () =>
      apiClient
        .get(`/credits/v2/forecast/${tenant}`)
        .then((r) => r.data as CreditForecast),
    staleTime: 60_000,
  })
}

// ── Golden rule ──────────────────────────────────────────────────────────────
// GET /api/v2/billing/golden-rule — the platform-wide guarantee that only AI
// functions consume credits (everything else in the plan is included).

export interface GoldenRule {
  /** Whether the golden rule is enforced for this tenant. */
  enabled: boolean
  /** Display message (FR primary). */
  message: string
  /** Optional list of credit-consuming feature categories. */
  ai_features?: string[]
}

/** The "seules les fonctions IA consomment des crédits" guarantee. */
export function useGoldenRule() {
  return useQuery({
    queryKey: ['credits', 'golden-rule'] as const,
    queryFn: () =>
      apiClient.get(API_ENDPOINTS.billingV2.goldenRule).then((r) => r.data as GoldenRule),
    staleTime: 30 * 60_000,
  })
}
